import {
  Button,
  Checkbox,
  ComposedModal,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Tooltip,
} from '@carbon/react';
import {
  CheckmarkFilled,
  CircleDash,
  CloseFilled,
  Information,
  Rocket,
} from '@carbon/react/icons';
import { useAtomValue } from 'jotai';

import { type TypeCurveWorkspaceResponse } from 'types/type-curve-workspace';
import type { WorkspaceTask } from 'types/type-curve-workspace-show';
import type { InventoryVerificationSummary } from 'pages/type-curves/types';

import { format0 } from 'utils/number';

import { tcWorkspaceTasksAtom, OVERALL_TYPE_CURVE_KEY } from './tasks';

import style from './tc-promote-workspace-modal.module.scss';

export interface TcPromoteWorkspaceModalProps {
  open: boolean;
  workspace: TypeCurveWorkspaceResponse | null;
  typeCurveLabel: string;
  loading: boolean;
  error: string | null;
  /** Why the promote was rejected (NOT_PROMOTABLE `details.incomplete`), surfaced
   * as a tooltip on the failure notification. */
  errorReasons?: string[];
  inventoryVerificationSummary: InventoryVerificationSummary | null;
  removedUnitsCount: number;
  /** Set as Default, owned by the caller because it is submitted with the promote. */
  setAsDefault: boolean;
  onSetAsDefaultChange: (checked: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
}

interface TaskGroupView {
  key: string;
  title: string;
  tasks: WorkspaceTask[];
}

function statusProps(status: string | null) {
  if (status === 'correct') {
    return {
      icon: CheckmarkFilled,
      label: 'Correct',
      className: style.taskStatusCorrect,
    };
  }
  if (status === 'incorrect') {
    return {
      icon: CloseFilled,
      label: 'Incorrect',
      className: style.taskStatusIncorrect,
    };
  }
  return {
    icon: CircleDash,
    label: 'Not marked',
    className: style.taskStatusNeutral,
  };
}

export const TcPromoteWorkspaceModal = ({
  open,
  workspace,
  typeCurveLabel,
  loading,
  error,
  errorReasons,
  inventoryVerificationSummary,
  removedUnitsCount,
  setAsDefault,
  onSetAsDefaultChange,
  onClose,
  onConfirm,
}: TcPromoteWorkspaceModalProps) => {
  const { tasks } = useAtomValue(tcWorkspaceTasksAtom);

  // What ticking the box would displace: the unique Type Curve id, then its well spacing, matching the
  // mock's two-part shape. The id rather than the workspace name because what loses the default is a
  // Type Curve, and a workspace name ("Workspace C") reads as the container instead.
  //
  // Spacing is shown even though the id conventionally embeds it (…_WCA_1320_…), because that is a
  // naming convention rather than a guarantee and well_spacing_ft is the real field.
  //
  // Falls back to the public id, then the workspace name, so the notification always names something
  // rather than rendering "will replace" against a blank.
  const zoneDefault = workspace?.zoneDefault ?? null;
  const zoneDefaultName =
    zoneDefault?.uniqueTcId ??
    zoneDefault?.tcPublicId ??
    zoneDefault?.name ??
    null;
  const zoneDefaultLabel = zoneDefaultName
    ? [
        zoneDefaultName,
        zoneDefault?.wellSpacingFt
          ? `${format0(zoneDefault.wellSpacingFt)} ft`
          : null,
      ]
        .filter(Boolean)
        .join(' \u2014 ')
    : null;

  // Overall Type Curve verdict is a task (set from the header), shown as the
  // "Your verification" badge — not a checklist row.
  const overallTask = tasks.find((t) => t.group === OVERALL_TYPE_CURVE_KEY);
  const verificationStatus = overallTask?.status ?? null;
  const isCorrect = verificationStatus === 'correct';
  const isIncorrect = verificationStatus === 'incorrect';
  const verificationLabel = isCorrect
    ? 'Correct'
    : isIncorrect
      ? 'Incorrect'
      : 'Not marked';

  // Checklist rows exclude the overall Type Curve task (mirrors the panel).
  const rowTasks = tasks.filter((t) => t.group !== OVERALL_TYPE_CURVE_KEY);
  const completedTaskCount = rowTasks.filter((t) => t.status !== null).length;

  const hasAnyAction = verificationStatus !== null || completedTaskCount > 0;

  // Summary of changes
  const summary = inventoryVerificationSummary;
  const hasSummary =
    (summary && summary.verdictCount > 0) || removedUnitsCount > 0;

  // Group tasks by backend group, preserving first-seen order.
  const groups: TaskGroupView[] = [];
  const byKey = new Map<string, TaskGroupView>();
  for (const task of rowTasks) {
    let group = byKey.get(task.group);
    if (!group) {
      group = { key: task.group, title: task.groupTitle, tasks: [] };
      byKey.set(task.group, group);
      groups.push(group);
    }
    group.tasks.push(task);
  }

  return (
    <ComposedModal
      open={open}
      onClose={() => {
        if (!loading) {
          onClose();
        }
      }}
      preventCloseOnClickOutside={loading}
      size="sm"
      className={style.modal}
    >
      <ModalHeader title="Promote" label={workspace?.name ?? 'Workspace'} />
      <ModalBody aria-label="Promote workspace verification">
        <div className={style.body}>
          <p className={style.message}>
            Promoting the verification status will result in the subject Type
            Curve <strong>({typeCurveLabel})</strong> to be verified across
            Insights. You and others can come back to{' '}
            <strong>{workspace?.name ?? 'Workspace'}</strong> to review it.
          </p>

          {error ? (
            /* The tooltip trigger is a sibling overlaid on the notification, not
               a child: InlineNotification throws on interactive children. */
            <div className={style.notificationWrap}>
              <InlineNotification
                kind="error"
                title="Promote failed"
                subtitle={error}
                lowContrast
                hideCloseButton
                className={style.notification}
              />
              {errorReasons?.length ? (
                <Tooltip
                  align="bottom"
                  autoAlign
                  className={style.notificationInfo}
                  label={
                    <div className={style.reasonsTooltip}>
                      Reasons:
                      <ul>
                        {errorReasons.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  }
                >
                  <button
                    type="button"
                    className={style.notificationInfoTrigger}
                    aria-label={`Reasons: ${errorReasons.join(', ')}`}
                  >
                    <Information size={16} />
                  </button>
                </Tooltip>
              ) : null}
            </div>
          ) : null}

          {/* Set as Default sits above the verification card, per the mock. Promoting does NOT make a
              Type Curve the zone default on its own — this box is the only thing that moves it, which
              is why the backend treats an absent flag as "leave the default alone". */}
          <div className={style.defaultBox}>
            <Checkbox
              id="tc-promote-set-as-default"
              labelText="Set as Default"
              checked={setAsDefault}
              onChange={(_e, { checked }) => onSetAsDefaultChange(checked)}
              disabled={loading}
            />
            {/* Only shown once ticked, and only when there is something to displace. A zone with no
                default yet has nothing to warn about. */}
            {setAsDefault && zoneDefaultLabel ? (
              <InlineNotification
                kind="info"
                lowContrast
                hideCloseButton
                className={style.defaultNotification}
                title={
                  zoneDefault?.sameFamily
                    ? 'Promoting to default will replace your current default'
                    : 'Promoting to default will replace current default'
                }
                /* String, not an element: Carbon types subtitle as string. The monospace treatment
                   the mock shows is applied to Carbon's own subtitle span from .defaultNotification. */
                subtitle={zoneDefaultLabel}
              />
            ) : null}
          </div>

          <div className={style.verificationBox}>
            <span className={style.verificationHeading}>Your verification</span>
            <span
              className={
                isCorrect
                  ? style.verificationStatusCorrect
                  : isIncorrect
                    ? style.verificationStatusIncorrect
                    : style.verificationStatusNeutral
              }
            >
              {isCorrect ? (
                <CheckmarkFilled size={16} aria-hidden />
              ) : isIncorrect ? (
                <CloseFilled size={16} aria-hidden />
              ) : (
                <CircleDash size={16} aria-hidden />
              )}
              {verificationLabel}
            </span>
          </div>

          {hasSummary ? (
            <div className={style.summaryBox}>
              <span className={style.summaryHeading}>Summary of changes</span>
              <ul className={style.summaryList}>
                {summary && summary.verdictCount > 0 ? (
                  <li>
                    {summary.verdictCount} of {summary.totalUnits} units
                    verified ({summary.correctCount} correct,{' '}
                    {summary.incorrectCount} incorrect)
                  </li>
                ) : null}
                {removedUnitsCount > 0 ? (
                  <li>
                    {removedUnitsCount} unit{removedUnitsCount !== 1 ? 's' : ''}{' '}
                    removed from the inventory
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}

          <div className={style.tasksBox}>
            <span className={style.tasksHeading}>
              All tasks ({completedTaskCount}/{rowTasks.length})
            </span>
            {groups.map((group) => (
              <div key={group.key}>
                <span className={style.taskGroupLabel}>{group.title}</span>
                {group.tasks.map((task) => {
                  const sp = statusProps(task.status);
                  const StatusIcon = sp.icon;

                  return (
                    <div key={task.path} className={style.taskRow}>
                      <span className={style.taskRowLeading}>
                        <StatusIcon
                          size={16}
                          className={sp.className}
                          aria-hidden
                        />
                        <span className={style.taskLabel}>{task.title}</span>
                      </span>
                      <span className={sp.className}>{sp.label}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          kind="secondary"
          className={style.cancelButton}
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={onConfirm}
          disabled={loading || !workspace || !hasAnyAction}
          renderIcon={Rocket}
        >
          {loading ? 'Promoting\u2026' : 'Promote'}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
